import { Component, Input, ViewChild, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, NgForm } from '@angular/forms';
import { MatPaginator, MatSort, MatTableDataSource } from '@angular/material';
import * as XLSX from 'xlsx';
import { AuthProvider } from '../../providers/auth/auth';
import { DatabaseProvider } from '../../providers/database/database';
import _ from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import { AlertController, LoadingController } from 'ionic-angular';
import { UtilityProvider } from '../../providers/utility/utility';
import { PatientProvider } from '../../providers/patient/patient';

@Component({
  selector: 'patient-clinical',
  templateUrl: 'patient-clinical.html'
})
export class PatientClinicalComponent {
  private ngUnsubscribe: Subject<any> = new Subject();
  program_id: string;
  program_name: string;
  patient: any;
  exportjson: any;
  private _program_id = new BehaviorSubject<any>([]);
  private _program_name = new BehaviorSubject<any>([]);
  private _patient = new BehaviorSubject<any>([]);

  @Input()
  set programId(value) { this._program_id.next(value); }
  get programId() { return this._program_id.getValue(); }

  @Input()
  set programName(value) { this._program_name.next(value); }
  get programName() { return this._program_name.getValue(); }

  @Input()
  set patientData(value) { this._patient.next(value); }
  get patientData() { return this._patient.getValue(); }

  userid: string;
  user_fullname: string;
  user: any;
  clinical: any[] = [];
  medications: any[] = [];
  clinicalPanelStatus = false;

  addeditClinicalForm: FormGroup; 

  displayedColumns = ['current_medications','previous_medications', 'other_medications', 'allergies', 'notes', 'bp', 'height', 'weight', 'enteredby', 'date'];
  dataSource: any;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('formClinicalDirective') private formClinicalDirective: NgForm;

  constructor(
    private readonly auth: AuthProvider,
    private readonly db: DatabaseProvider,
    private dbPatient: PatientProvider,
    private fb: FormBuilder,
    public alertCtrl: AlertController,
    public utility: UtilityProvider,
    public loadingCtrl: LoadingController
    ) {
      this.createForm();

      this.auth.getCurrentUser().then(user => {
        this.userid = user.id;
        this.user_fullname = user.first_name + ' ' + user.last_name;
        this.user = { id: user.id, name: user.first_name + ' ' + user.last_name };

        this.db
        .getAdminSettings(this.program_id)
        .snapshotChanges()
        .map(actions => {
          return actions.map(a => {
            const data = a.payload.doc.data() as any;
            data.id = a.payload.doc.id;
            return data;
          });
        })
        .takeUntil(this.ngUnsubscribe)
        .subscribe((settings) => {
          this.medications = settings[3]['previous_medications'];
        });

        this.dbPatient
        .listClinical(this.program_id, this.patient.id)
        .snapshotChanges()
        .map(actions => {
          return actions.map(a => {
            const data = a.payload.doc.data() as any;
            data.id = a.payload.doc.id;
            return data;
          });
        })
        .takeUntil(this.ngUnsubscribe)
        .subscribe(clinical => {
          this.exportjson = clinical;
          this.dataSource = new MatTableDataSource(clinical);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
        });
      });
    }

  ngOnInit()  {
    this._program_id
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
        this.program_id = x
    });

    this._program_name
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
        this.program_name = x
    });

    this._patient
    .takeUntil(this.ngUnsubscribe)
    .subscribe(x => {
        this.patient = x
    });
  }

  ngOnDestroy() {
    console.log('Leave patient clinical and destroy subscriptions');
    this._patient.unsubscribe();
    this._program_id.unsubscribe();
    this._program_name.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim(); // Remove whitespace
    filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
    this.dataSource.filter = filterValue;
  }

  export() {
    const workBook = XLSX.utils.book_new(); // create a new blank book
    const workSheet = XLSX.utils.json_to_sheet(this.exportjson);

    XLSX.utils.book_append_sheet(workBook, workSheet, 'Clinical'); // add the worksheet to the book
    XLSX.writeFile(workBook, 'clinical.xlsx'); // initiate a file download in browser
  }

  createForm()  {
    let emailregex: RegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    let phoneregex: RegExp = /^\+[1-9]\d{1,14}(\se?x?t?(\d*))?$/

    this.addeditClinicalForm = this.fb.group({
      'allergies': [null],
      'current_medications': [null],
      'previous_medications': [null],
      'other_medications': [null],
      'notes': [null],
      'date': [null, [Validators.required]],
      'bp_systolic': [null],
      'bp_diastolic': [null],
      'height': [null],
      'weight': [null]
    });
    this.addeditClinicalForm.controls['date'].setValue(new Date());
  }

  save()  {
    const loading = this.loadingCtrl.create({});
    loading.present();
    let clinical = this.addeditClinicalForm.value;
    const patient = {
      id: this.patient.id,
      first_name: this.patient.first_name,
      last_name: this.patient.last_name,
      program_patient_id: this.patient.program_patient_id
    }
    clinical = { ...clinical, patient: patient };
    this.dbPatient.addClinical(this.program_id, this.patient.id, clinical, this.user).then(() => {
      this.cancel();
      loading.dismiss();
      this.utility.presentToast('Clinical entry was successfully saved');
    })
    .catch(err => {
      this.utility.presentToast('There was a problem saving this clinical entry.  Please try again. Error: ' + err);
      loading.dismiss();
    });
  }

  cancel()  {
    this.clinicalPanelStatus = false;
    setTimeout(() => {
      this.formClinicalDirective.resetForm();
      this.addeditClinicalForm.reset();
      this.addeditClinicalForm.controls['date'].setValue(new Date());
    }, 0);
  }
}



// WEBPACK FOOTER //
// ./src/components/patient-clinical/patient-clinical.ts